import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import type { ResearchTopic, ViewMode } from '../types'
import type { ResearchDepth } from '@/types/intelligence'
import HeroSection from '../components/landing/HeroSection'
import StackedFeatureCards from '../components/landing/StackedFeatureCards'
import MobileAppBanner from '../components/shared/MobileAppBanner'
import { API_BASE_URL, fetchPublicExploreCards } from '../lib/api'
import type { ResearchCard } from '../lib/exploreApi'
import { analytics } from '../lib/analytics'
import {
  dispatchPodcastPlayback,
  getPodcastActionState,
  resolveIntelligencePodcastPlayback,
} from '../lib/podcastPlayback'

// Hook for intersection observer (scroll-triggered reveals)
function useScrollReveal(options: IntersectionObserverInit = {}) {
  const ref = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.unobserve(element) // Only trigger once
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px', ...options }
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  return { ref, isVisible }
}

// Storage key for persisting query through login
const PENDING_RESEARCH_KEY = 'pendingResearch'
const DEFAULT_HERO_PLACEHOLDER = "What's the outlook for nuclear energy investments?"

// 100+ rotating placeholder examples covering diverse industries and use cases
const EXAMPLE_PROMPTS = [
  // AI & Technology
  "Map the competitive landscape for AI research agents in 2025.",
  "How are enterprise companies adopting LLMs for internal workflows?",
  "Compare Claude, GPT-4, and Gemini for code generation tasks.",
  "What are the key players in AI infrastructure and MLOps?",
  "Deep dive: How does retrieval-augmented generation (RAG) work?",
  "Analyze the AI chip market: Nvidia vs AMD vs custom silicon.",
  "Which startups are building AI agents for sales automation?",
  "How is AI transforming drug discovery and clinical trials?",
  "Compare vector databases: Pinecone, Weaviate, Chroma, Milvus.",
  "What's the state of open-source LLMs in 2025?",
  
  // Finance & Fintech
  "Compare Stripe vs Adyen for enterprise payments: pricing + positioning.",
  "How are banks using AI for fraud detection?",
  "Analyze the buy-now-pay-later market: Affirm, Klarna, Afterpay.",
  "What's driving the growth of embedded finance?",
  "Compare neobanks: Chime, Revolut, N26 business models.",
  "How is blockchain being used in trade finance?",
  "Analyze the wealth management technology landscape.",
  "What are the key players in B2B payments infrastructure?",
  "How are insurance companies adopting AI underwriting?",
  "Compare stock trading platforms for retail investors.",
  
  // Healthcare & Biotech
  "Summarize the latest evidence on GLP-1s and long-term outcomes.",
  "How is AI being used for medical imaging diagnosis?",
  "Analyze the telemedicine market post-pandemic.",
  "What are the leading companies in CRISPR therapeutics?",
  "Compare electronic health record (EHR) systems for hospitals.",
  "How is personalized medicine evolving with genomics?",
  "Analyze the mental health tech startup ecosystem.",
  "What's the state of AI in radiology and pathology?",
  "How are wearables being used for clinical trials?",
  "Compare digital therapeutics companies and their evidence.",
  
  // Enterprise Software
  "Due diligence: top observability vendors for startups using Render.",
  "Compare Salesforce vs HubSpot for mid-market companies.",
  "How is the data warehouse market evolving? Snowflake vs Databricks.",
  "Analyze the low-code/no-code platform landscape.",
  "Compare customer data platforms: Segment, mParticle, Rudderstack.",
  "What are the key players in API management?",
  "How is the CRM market consolidating?",
  "Compare cloud security posture management (CSPM) tools.",
  "Analyze the product analytics landscape: Amplitude, Mixpanel, Heap.",
  "What's driving adoption of composable commerce?",
  
  // Markets & Economics
  "Deep dive: Nvidia's data center growth drivers and key risks.",
  "How is the semiconductor supply chain restructuring?",
  "Analyze the electric vehicle battery supply chain.",
  "What's driving the growth of the defense tech sector?",
  "How are interest rates affecting commercial real estate?",
  "Analyze the private credit market growth.",
  "What are the key drivers of US manufacturing reshoring?",
  "How is the luxury goods market performing in Asia?",
  "Analyze the hydrogen economy investment landscape.",
  "What's the outlook for nuclear energy investments?",
  
  // Consumer & Retail
  "How is social commerce evolving on TikTok and Instagram?",
  "Analyze the direct-to-consumer brand landscape in 2025.",
  "Compare grocery delivery models: Instacart vs DoorDash vs Gopuff.",
  "How are retailers using AI for inventory management?",
  "What's driving the growth of subscription commerce?",
  "Analyze the resale and secondhand market.",
  "How is augmented reality being used in retail?",
  "Compare loyalty program strategies across retail sectors.",
  "What's the state of voice commerce?",
  "How are brands approaching sustainability marketing?",
  
  // Climate & Energy
  "Analyze the carbon capture technology landscape.",
  "How is the solar industry supply chain evolving?",
  "Compare battery storage solutions for utilities.",
  "What are the key players in green hydrogen production?",
  "How is the voluntary carbon market structured?",
  "Analyze the electric vehicle charging infrastructure.",
  "What's driving investment in fusion energy startups?",
  "How are data centers approaching sustainability?",
  "Compare offshore wind developers and their portfolios.",
  "What's the state of sustainable aviation fuel?",
  
  // Real Estate & Construction
  "How is proptech transforming commercial real estate?",
  "Analyze the construction technology startup landscape.",
  "What's driving the growth of industrial real estate?",
  "How are REITs adapting to the work-from-home trend?",
  "Compare property management software platforms.",
  "What's the outlook for multifamily housing?",
  "How is 3D printing being used in construction?",
  "Analyze the co-living and co-working market.",
  "What are the key players in real estate tokenization?",
  "How is climate risk affecting real estate valuations?",
  
  // Education & HR
  "How is AI transforming corporate learning and development?",
  "Analyze the online education market evolution.",
  "Compare HR tech platforms: Workday, BambooHR, Gusto.",
  "What's driving the skills-based hiring movement?",
  "How are companies approaching employee wellness?",
  "Analyze the coding bootcamp market and outcomes.",
  "What are the key players in talent intelligence?",
  "How is the gig economy workforce being managed?",
  "Compare performance management software approaches.",
  "What's the state of diversity and inclusion tech?",
  
  // Media & Entertainment
  "How are streaming services evolving their content strategies?",
  "Analyze the creator economy infrastructure.",
  "What's driving growth in gaming and esports?",
  "How is AI being used in music and video production?",
  "Compare podcast monetization strategies.",
  "What's the state of virtual and augmented reality entertainment?",
  "How are news organizations adapting to AI?",
  "Analyze the influencer marketing platform landscape.",
  "What are the key players in digital advertising?",
  "How is the sports betting market evolving?",
  
  // Transportation & Logistics
  "Analyze the autonomous vehicle technology landscape.",
  "How is the freight and logistics market digitizing?",
  "Compare electric vehicle manufacturers by market segment.",
  "What's driving the growth of last-mile delivery?",
  "How are airlines using AI for operations?",
  "Analyze the urban air mobility (eVTOL) market.",
  "What are the key players in fleet management software?",
  "How is the shipping industry decarbonizing?",
  "Compare ride-sharing models globally.",
  "What's the state of hyperloop and high-speed rail?",
  
  // Government & Policy
  "How are governments regulating AI development?",
  "Analyze the cybersecurity policy landscape.",
  "What's driving the growth of govtech?",
  "How are cities approaching smart city initiatives?",
  "What are the key infrastructure bill implications?",
  "How is the defense industry adopting commercial tech?",
  "Analyze the space policy and regulation landscape.",
  "What's the state of digital identity regulations?",
  "How are privacy regulations evolving globally?",
  "What changed in US manufacturing jobs from 2005–2025? Sources + chart.",
]

export interface PendingResearch {
  query: string
  depth: ResearchDepth
  isPublic: boolean
  timestamp: number
}

// Export function to get and clear pending research (used by App after login)
export function getPendingResearch(): PendingResearch | null {
  try {
    const stored = localStorage.getItem(PENDING_RESEARCH_KEY)
    if (!stored) return null
    
    const data = JSON.parse(stored) as PendingResearch
    // Expire after 30 minutes
    if (Date.now() - data.timestamp > 30 * 60 * 1000) {
      localStorage.removeItem(PENDING_RESEARCH_KEY)
      return null
    }
    return data
  } catch {
    return null
  }
}

export function clearPendingResearch(): void {
  localStorage.removeItem(PENDING_RESEARCH_KEY)
}

// Custom hook for reduced motion preference
function usePrefersReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)
  
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReducedMotion(mediaQuery.matches)
    
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches)
    mediaQuery.addEventListener('change', handler)
    return () => mediaQuery.removeEventListener('change', handler)
  }, [])
  
  return prefersReducedMotion
}

// Shuffle array using Fisher-Yates algorithm
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

interface LandingPageProps {
  viewMode: ViewMode
  onLogin: () => void
  onGetStarted: () => void
}

// Parallax Card Component - scroll-snap section with expanding effect
interface ParallaxCardProps {
  children: React.ReactNode
  variant?: 'dark' | 'darker' | 'charcoal' | 'obsidian'
}

const ParallaxCard = ({ children, variant = 'dark' }: ParallaxCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null)
  const [scrollProgress, setScrollProgress] = useState(0)
  
  useEffect(() => {
    const card = cardRef.current
    if (!card) return
    
    const handleScroll = () => {
      const rect = card.getBoundingClientRect()
      const windowHeight = window.innerHeight
      
      // Calculate how far into the viewport the card is
      // 0 = just entering from bottom, 1 = fully visible at top
      const progress = 1 - (rect.top / windowHeight)
      const clampedProgress = Math.max(0, Math.min(1, progress))
      setScrollProgress(clampedProgress)
    }
    
    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll() // Initial check
    
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])
  
  // Calculate scale: starts at 0.92, expands to 1.0
  const scale = 0.92 + (scrollProgress * 0.08)
  // Calculate border radius: starts at 40px, reduces to 0
  const borderRadius = 40 * (1 - scrollProgress)
  
  return (
    <section className="snap-section">
      <div 
        ref={cardRef}
        className={`expanding-card expanding-card--${variant}`}
        style={{
          transform: `scale(${scale})`,
          borderRadius: `${borderRadius}px`,
        }}
      >
        <div className="expanding-card-content">
          {children}
        </div>
      </div>
    </section>
  )
}

// Feature Row Component (inline feature with icon)
interface FeatureRowProps {
  icon: React.ReactNode
  text: string
}

const FeatureRow = ({ icon, text }: FeatureRowProps) => (
  <div className="flex items-center gap-3 md:gap-4">
    <div className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/70">
      {icon}
    </div>
    <p className="text-white/80 text-sm md:text-lg">{text}</p>
  </div>
)

// Stat Item Component
interface StatItemProps {
  value: string
  label: string
}

const StatItem = ({ value, label }: StatItemProps) => (
  <div className="parallax-stat">
    <div className="parallax-stat-value">{value}</div>
    <div className="parallax-stat-label">{label}</div>
  </div>
)

// ── Public Reports Feed ───────────────────────────────────────────────────────

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays}d ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`
  return `${Math.floor(diffDays / 30)}mo ago`
}

interface PublicReportsFeedProps {
  variant?: 'dark' | 'light'
  onExploreMore: () => void
  viewMode: ViewMode
}

type LandingReportCard = ResearchCard & { intelligence_report_id: string }
const LANDING_PUBLIC_REPORT_LIMIT = 5

function isLandingIntelligenceCard(card: ResearchCard): card is LandingReportCard {
  return Boolean(card.is_intelligence_report && card.intelligence_report_id)
}

function buildLandingPodcastTopic(card: LandingReportCard): ResearchTopic {
  return {
    id: String(card.id),
    title: card.title || 'Intelligence Report',
    description: card.summary || card.original_query || 'Intelligence report',
    summary: card.summary || '',
    category: card.category || 'General',
    trending_percentage: 0,
    engagement_count: 0,
    like_count: card.like_count,
    liked_by_user: card.liked_by_user,
    sources: [],
    created_at: card.created_at || new Date().toISOString(),
    image_url: card.image_url || undefined,
    tags: [],
    query: card.original_query || undefined,
    author: card.author_name ? { name: card.author_name } : undefined,
    is_intelligence_report: true,
    intelligence_report_id: card.intelligence_report_id,
    podcast_status: card.podcast_status,
    podcast_audio_ready: card.podcast_audio_ready,
    podcast_error_message: card.podcast_error_message ?? undefined,
  }
}

function openCanonicalReport(reportId: string) {
  if (typeof window === 'undefined') return
  window.history.pushState({ reportId }, '', `/report/${reportId}`)
  window.dispatchEvent(new Event('popstate'))
}

const PublicReportsFeed = ({ variant = 'light', onExploreMore, viewMode }: PublicReportsFeedProps) => {
  const [cards, setCards] = useState<LandingReportCard[]>([])
  const [loading, setLoading] = useState(true)
  const [pendingListenByReport, setPendingListenByReport] = useState<Record<string, boolean>>({})
  const isMobile = viewMode === 'mobile'

  useEffect(() => {
    let isMounted = true

    fetchPublicExploreCards(24)
      .then((results) => {
        if (!isMounted) return
        setCards((results as ResearchCard[]).filter(isLandingIntelligenceCard))
      })
      .catch(() => {
        if (!isMounted) return
        setCards([])
      })
      .finally(() => {
        if (isMounted) setLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [])

  const updateCardPodcastState = useCallback((reportId: string, podcastInfo: any) => {
    setCards((prev) =>
      prev.map((card) =>
        card.intelligence_report_id === reportId
          ? {
              ...card,
              podcast_status:
                (podcastInfo.audio_url ? 'ready' : podcastInfo.status) ||
                card.podcast_status ||
                'missing',
              podcast_audio_ready: Boolean(podcastInfo.audio_url),
              podcast_duration_seconds:
                podcastInfo.duration_seconds ?? card.podcast_duration_seconds,
              podcast_error_message: podcastInfo.error_message ?? undefined,
            }
          : card
      )
    )
  }, [])

  const handleListen = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>, card: LandingReportCard) => {
      event.stopPropagation()

      const reportId = card.intelligence_report_id
      const actionState = getPodcastActionState(buildLandingPodcastTopic(card)) ?? {
        disabled: true,
      }
      if (!reportId || actionState.disabled || pendingListenByReport[reportId]) {
        return
      }

      analytics.trackPodcastPlayed({
        research_id: String(card.id),
        report_id: reportId,
        source: 'landing_page' as any,
        duration_listened_seconds: 0,
        completion_rate_percent: 0,
      })

      setPendingListenByReport((prev) => ({ ...prev, [reportId]: true }))

      try {
        const podcastInfo = await resolveIntelligencePodcastPlayback(reportId, {
          onPending: (pending) => updateCardPodcastState(reportId, pending),
        })
        updateCardPodcastState(reportId, podcastInfo)

        if (!podcastInfo.audio_url) {
          return
        }

        dispatchPodcastPlayback(buildLandingPodcastTopic(card), podcastInfo)
      } catch (error) {
        console.error('Failed to start landing-page podcast playback:', error)
      } finally {
        setPendingListenByReport((prev) => ({ ...prev, [reportId]: false }))
      }
    },
    [pendingListenByReport, updateCardPodcastState]
  )

  const handleExploreMore = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem('insightatlas-return-url', '#explore')
    }
    onExploreMore()
  }, [onExploreMore])

  if (!loading && cards.length === 0) return null

  const isDark = variant === 'dark'
  const previewCards = cards.slice(0, LANDING_PUBLIC_REPORT_LIMIT)
  const scrollHintCopy = isMobile ? 'Swipe for more reports' : 'Scroll horizontally to see more'

  return (
    <section
      className="snap-section landing-public-feed-section"
      style={{ background: isDark ? '#0f0f0f' : '#f8fafc' }}
    >
      <div className="landing-public-feed-shell w-full max-w-7xl mx-auto px-4 md:px-6 flex flex-col justify-start">
        <div className="public-feed-header mb-8 md:mb-10">
          <div>
            <p className={`text-xs font-semibold tracking-[0.2em] uppercase mb-2 ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
              Live Intelligence
            </p>
            <h2 className="public-feed-heading">Browse public research.</h2>
            <p className="public-feed-subheading">Open the full report or start the AI podcast without creating an account.</p>
          </div>
          <div className="public-feed-scroll-hint" aria-label="Horizontal scroll hint">
            <span>{scrollHintCopy}</span>
            <svg className="public-feed-scroll-hint-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M4 12h16" />
            </svg>
          </div>
        </div>

        <div
          aria-label="Public reports rail"
          className="public-feed-scroll"
          role="region"
        >
          {loading
            ? Array.from({ length: LANDING_PUBLIC_REPORT_LIMIT }).map((_, i) => (
                <div key={i} className="public-report-skeleton">
                  <div className="public-report-skeleton-thumb" />
                  <div className="public-report-skeleton-body">
                    <div className="public-report-skeleton-line" style={{ width: '28%' }} />
                    <div className="public-report-skeleton-line" style={{ width: '84%' }} />
                    <div className="public-report-skeleton-line" style={{ width: '92%' }} />
                    <div className="public-report-skeleton-line" style={{ width: '70%' }} />
                    <div className="public-report-skeleton-actions">
                      <div className="public-report-skeleton-pill" />
                      <div className="public-report-skeleton-pill" />
                    </div>
                  </div>
                </div>
              ))
            : previewCards.map((card, index) => {
                const listenAction = getPodcastActionState(buildLandingPodcastTopic(card)) ?? {
                  disabled: true,
                  showSpinner: false,
                }
                const isListenPending =
                  Boolean(pendingListenByReport[card.intelligence_report_id]) ||
                  Boolean(listenAction.showSpinner)

                return (
                  <article
                    key={card.id}
                    className="public-report-card"
                    onClick={() => {
                      analytics.trackExploreCardClicked({
                        research_id: String(card.id),
                        report_id: card.intelligence_report_id,
                        position: index,
                        category: card.category ?? null,
                        has_image: Boolean(card.image_url),
                        source: 'landing_page',
                      })
                      openCanonicalReport(card.intelligence_report_id)
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        analytics.trackExploreCardClicked({
                          research_id: String(card.id),
                          report_id: card.intelligence_report_id,
                          position: index,
                          category: card.category ?? null,
                          has_image: Boolean(card.image_url),
                          source: 'landing_page',
                        })
                        openCanonicalReport(card.intelligence_report_id)
                      }
                    }}
                    role="link"
                    tabIndex={0}
                    aria-label={`Open full report: ${card.title}`}
                  >
                    <div className="public-report-thumb">
                      <img
                        src={`${API_BASE_URL}/api/intelligence/report/${card.intelligence_report_id}/image`}
                        alt=""
                        loading="lazy"
                        onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
                      />
                      <div className="public-report-thumb-overlay" />
                      <div className="public-report-thumb-copy">
                        <span className="public-report-thumb-label">Public report</span>
                        <p className="public-report-thumb-title">{card.title}</p>
                      </div>
                    </div>
                    <div className="public-report-body">
                      {card.category && (
                        <span className="public-report-badge">{card.category}</span>
                      )}
                      <p className="public-report-title">{card.title}</p>
                      <p className="public-report-summary">
                        {card.summary ||
                          card.original_query ||
                          'Open the full report to inspect the analysis, evidence, and AI-generated podcast.'}
                      </p>
                      <div className="public-report-meta">
                        <span className="public-report-author">
                          {card.author_name || 'Anonymous'}
                        </span>
                        <span className="public-report-date">
                          {formatRelativeDate(card.created_at)}
                        </span>
                      </div>
                      <div className="public-report-actions">
                        <button
                          type="button"
                          className="public-report-button public-report-button--secondary"
                          onClick={(event) => handleListen(event, card)}
                          disabled={
                            Boolean(pendingListenByReport[card.intelligence_report_id]) ||
                            Boolean(listenAction.disabled)
                          }
                        >
                          {isListenPending ? 'Preparing...' : 'Listen'}
                        </button>
                        <button
                          type="button"
                          className="public-report-button public-report-button--primary"
                          onClick={(event) => {
                            event.stopPropagation()
                            openCanonicalReport(card.intelligence_report_id)
                          }}
                        >
                          Read
                        </button>
                      </div>
                    </div>
                  </article>
                )
              }).concat(
                <article
                  key="see-more-cta"
                  className="public-report-card public-report-card--cta"
                  onClick={handleExploreMore}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      handleExploreMore()
                    }
                  }}
                  role="link"
                  tabIndex={0}
                  aria-label="See more public research in Explore after login"
                >
                  <div className="public-report-cta-shell">
                    <span className="public-report-cta-kicker">Continue in Insight Atlas</span>
                    <h3 className="public-report-cta-title">See more research</h3>
                    <p className="public-report-cta-copy">
                      Log in to open Explore and browse the full stream of public intelligence reports.
                    </p>
                    <div className="public-report-cta-action">
                      <span>See more</span>
                      <svg className="public-report-cta-arrow" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M4 12h16" />
                      </svg>
                    </div>
                  </div>
                </article>
              )}
        </div>
      </div>
    </section>
  )
}

export default function LandingPage({ viewMode, onLogin, onGetStarted }: LandingPageProps) {
  const [researchQuery, setResearchQuery] = useState('')
  const [isPublic, setIsPublic] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [placeholderIndex, setPlaceholderIndex] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [displayedPlaceholder, setDisplayedPlaceholder] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const landingViewTrackedRef = useRef(false)
  const prefersReducedMotion = usePrefersReducedMotion()
  
  // Always use maximum research depth - no selection needed
  const selectedDepth: ResearchDepth = 'maximum'
  const trimmedQuery = researchQuery.trim()
  const queryValid = trimmedQuery.length >= 10

  const isMobile = viewMode === 'mobile'

  // Keep the hero's first placeholder stable, then rotate through the remaining prompts.
  const shuffledExamples = useMemo(() => {
    const remainingExamples = EXAMPLE_PROMPTS.filter(prompt => prompt !== DEFAULT_HERO_PLACEHOLDER)
    return [DEFAULT_HERO_PLACEHOLDER, ...shuffleArray(remainingExamples)]
  }, [])

  // Initialize displayed placeholder
  useEffect(() => {
    setDisplayedPlaceholder(shuffledExamples[0] || '')
  }, [shuffledExamples])

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    if (landingViewTrackedRef.current) return
    landingViewTrackedRef.current = true

    analytics.trackLandingPageViewed({
      surface: 'landing',
      view_mode: viewMode,
    })
  }, [viewMode])

  // Track if we're in dark section (past hero)
  const [isInDarkSection, setIsInDarkSection] = useState(false)
  
  // Handle scroll effects
  useEffect(() => {
    const handleScroll = () => {
      // Get scroll position from the scroll-snap container
      const container = document.querySelector('.scroll-snap-page')
      const scrollY = container ? container.scrollTop : window.scrollY
      setIsScrolled(scrollY > 50)
      
      // Check if we've scrolled past the hero section (roughly 80% of viewport height)
      const heroThreshold = window.innerHeight * 0.8
      setIsInDarkSection(scrollY > heroThreshold)
    }
    
    // Listen to both window and container scroll
    const container = document.querySelector('.scroll-snap-page')
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true })
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll)
      }
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // Rotate placeholder with smooth fade transition
  useEffect(() => {
    if (prefersReducedMotion || researchQuery.length > 0) return
    
    const interval = setInterval(() => {
      // Start transition (fade out)
      setIsTransitioning(true)
      
      // After fade out, change text and fade in
      setTimeout(() => {
        setPlaceholderIndex(prev => (prev + 1) % shuffledExamples.length)
        setDisplayedPlaceholder(shuffledExamples[(placeholderIndex + 1) % shuffledExamples.length])
        setIsTransitioning(false)
      }, 350) // Match CSS transition duration
    }, 4000) // Rotate every 4 seconds
    
    return () => clearInterval(interval)
  }, [prefersReducedMotion, researchQuery.length, shuffledExamples, placeholderIndex])

  useEffect(() => {
    if (!textareaRef.current) return

    textareaRef.current.style.height = 'auto'
    const scrollHeight = textareaRef.current.scrollHeight
    const minHeight = isMobile ? 88 : 128
    const maxHeight = isMobile ? 240 : 360
    const nextHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight)

    textareaRef.current.style.height = `${nextHeight}px`
    textareaRef.current.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden'
  }, [researchQuery, isMobile])

  const handleStartResearch = useCallback(() => {
    const trimmed = researchQuery.trim()
    if (!trimmed || trimmed.length < 10) return
    analytics.trackAuthCtaClicked({ source: 'landing_page', cta: 'start_research', query_length: trimmed.length })
    // Store the research intent in localStorage
    const pendingResearch: PendingResearch = {
      query: trimmed,
      depth: selectedDepth,
      isPublic,
      timestamp: Date.now(),
    }
    localStorage.setItem(PENDING_RESEARCH_KEY, JSON.stringify(pendingResearch))
    
    // Redirect to login
    onLogin()
  }, [researchQuery, selectedDepth, isPublic, onLogin])

  const shouldAutoScrollHeroCards = isMobile && !prefersReducedMotion
  const handleScrollHome = useCallback(() => {
    const container = document.querySelector('.scroll-snap-page')
    if (container) container.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])
  const handleResearchQueryChange = useCallback((value: string) => {
    if (value.length <= 1500) setResearchQuery(value)
  }, [])

  return (
    <div className="scroll-snap-page bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
      <MobileAppBanner viewMode={viewMode} />
      <HeroSection
        isMobile={isMobile}
        isScrolled={isScrolled}
        isInDarkSection={isInDarkSection}
        researchQuery={researchQuery}
        displayedPlaceholder={displayedPlaceholder}
        isTransitioning={isTransitioning}
        isPublic={isPublic}
        queryValid={queryValid}
        shouldAutoScrollHeroCards={shouldAutoScrollHeroCards}
        textareaRef={textareaRef}
        onHome={handleScrollHome}
        onLogin={onLogin}
        onGetStarted={onGetStarted}
        onResearchQueryChange={handleResearchQueryChange}
        onSelectPrivate={() => setIsPublic(false)}
        onSelectPublic={() => setIsPublic(true)}
        onStartResearch={handleStartResearch}
      />

      <section className="landing-feature-section landing-feature-section--stacked">
        <div className="landing-feature-layout w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="landing-feature-copy">
            <p className="landing-feature-kicker">How the answer builds</p>
            <div className="landing-feature-intro">
              <p className="landing-feature-intro-lede">One report, revealed in layers.</p>
              <p className="landing-feature-intro-support">
                Insight Atlas starts with the decision-ready takeaway, then lets you move deeper into evidence, scenarios, and the full brief as you scroll.
              </p>
            </div>
          </div>
          <StackedFeatureCards reducedMotion={prefersReducedMotion} />
        </div>
      </section>

      {/* Public Reports Feed — replaces old Cards 1 & 2 */}
      <PublicReportsFeed variant="light" onExploreMore={onLogin} viewMode={viewMode} />

      {/* PARALLAX STACKED CARD SECTIONS */}
      <div className="parallax-container">

        {/* Card 3: Trust & Sharing */}
        <ParallaxCard variant="obsidian">
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-16 items-center w-full">
            {/* Content */}
            <div>
              <h2 className="parallax-heading">
                Research you can{' '}
                <span className="parallax-gradient-indigo">trust</span>,
                <br className="hidden sm:block" /><span className="sm:hidden"> </span>delivered fast.
              </h2>
              <p className="parallax-subheading">
                Send a report to a teammate or client in seconds—or save someone else's research to read later and build on it.
              </p>
              
              <div className="space-y-4 md:space-y-6">
                <FeatureRow 
                  icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>}
                  text="Save reports to your library"
                />
                <FeatureRow 
                  icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" /></svg>}
                  text="Share via link instantly"
                />
                <FeatureRow 
                  icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>}
                  text="Control visibility: public or private"
                />
              </div>
            </div>
            
            {/* Mockup - hidden on mobile/tablet to fit content */}
            <div className="hidden lg:block">
              <div className="parallax-mockup">
                <div className="parallax-mockup-header">
                  <div className="parallax-mockup-dot parallax-mockup-dot--red" />
                  <div className="parallax-mockup-dot parallax-mockup-dot--yellow" />
                  <div className="parallax-mockup-dot parallax-mockup-dot--green" />
                </div>
                <div className="parallax-mockup-content">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500/30 to-purple-500/30" />
                      <div className="parallax-mockup-line" style={{ width: '80px' }} />
                    </div>
                    <div className="flex gap-2">
                      <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                        <svg className="w-4 h-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                        </svg>
                      </div>
                      <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                        <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="parallax-mockup-line" style={{ width: '100%' }} />
                  <div className="parallax-mockup-line" style={{ width: '75%' }} />
                  <div className="h-24 rounded-xl bg-gradient-to-br from-indigo-500/10 to-purple-500/10 mt-4" />
                </div>
              </div>
            </div>
          </div>
          
          {/* Ambient glow */}
          <div className="parallax-glow parallax-glow--indigo" style={{ width: '450px', height: '450px', bottom: '10%', left: '20%' }} />
        </ParallaxCard>

        {/* Card 4: Stats */}
        <ParallaxCard variant="darker">
          <div className="flex flex-col items-center justify-center h-full">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="parallax-heading">
                Built for professionals who value their time.
              </h2>
            </div>
            
            <div className="parallax-stats">
              <StatItem value="10+" label="Page Reports" />
              <StatItem value="50+" label="Sources Analyzed" />
              <StatItem value="45-75" label="Minutes" />
              <StatItem value="100%" label="Cited" />
            </div>
            
            <div className="parallax-quote">
              Finally, research that respects both depth and my schedule.
              <div className="parallax-quote-author">— Every busy professional</div>
            </div>
          </div>
        </ParallaxCard>

        {/* Card 5: CTA + Footer */}
        <ParallaxCard variant="dark">
          <div className="flex flex-col items-center justify-between min-h-full w-full max-w-full">
            {/* CTA Section */}
            <div className="parallax-cta flex-1 flex flex-col items-center justify-center w-full">
              <h2 className="parallax-heading mb-4 md:mb-8 text-center px-4">
                Ready to transform how you research?
              </h2>
              <button 
                onClick={onGetStarted}
                className="parallax-cta-button"
              >
                Get Started Free
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </div>
            
            {/* Footer */}
            <footer className="w-full pt-8 md:pt-16 pb-8 border-t border-white/10 mt-auto">
              <div className="flex flex-col items-center text-center">
                <div className="flex items-center gap-3 mb-4">
                  <img 
                    src="/logo192.png" 
                    alt="Insight Atlas" 
                    className="w-10 h-10 rounded-xl"
                  />
                  <span className="text-xl font-bold text-white">Insight Atlas</span>
                </div>
                <p className="text-white/70 mb-4">
                  <a href="/about.html" className="underline">How Insight Atlas works</a>
                  {' · '}Built by <a href="https://www.outlierlabs.io/#products" className="inline-block align-middle rounded-md">
                    <img src="/brands/outlier-labs-golden-opal-publisher.png" alt="Outlier Labs Inc." width="160" height="32" loading="lazy" />
                  </a> in San Francisco, California.
                </p>
                
                <a
                  href="mailto:admin@outlierlabs.io"
                  className="inline-flex items-center gap-2 text-white/40 hover:text-white transition-colors mb-4"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Founder inquiries · admin@outlierlabs.io
                </a>
                
                <div className="flex items-center gap-1.5 text-white/30 text-sm">
                  <span>&copy; {new Date().getFullYear()} Insight Atlas</span>
                  <span>&middot;</span>
                  <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="hover:text-white/60 transition-colors">Terms</a>
                  <span>&middot;</span>
                  <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="hover:text-white/60 transition-colors">Privacy</a>
                </div>
              </div>
            </footer>
          </div>
        </ParallaxCard>

      </div>
    </div>
  )
}
